import { useEffect, useState } from "react";
import { DAY_MS, LogSet as LogSetSchema, type LogSet } from "@jackedlogs/shared";

import type { ProgressApiFetch } from "./types";

// THE "lazy-fetch a trailing window of raw LogSets" hook (CN-window-sets).
// Lifted out of FatigueDriftPanel when CO-10's effort-coverage panel became
// the second callsite: cancel-on-unmount, per-row safeParse (one malformed
// row can't blank a panel), and the trailing-window `since` are the parts
// that must never drift between callsites — a cancel-race fixed in one copy
// and not the other is exactly the latent bug §CONSOLIDAMENTO targets.
//
// The two things that genuinely differ per caller are parameters, not forks:
//   • makePath — WHICH route serves the window. /api/sets is self-only; the
//     coach surface must go through /api/sessions?include=sets, the one that
//     resolveCoachReadTarget gates (see Coach/athleteScopedApi.ts).
//   • extractRaw — how raw sets sit in that route's envelope (flat list vs
//     nested under each session).
export function useWindowSets(
  apiFetch: ProgressApiFetch,
  days: number,
  makePath: (sinceIso: string) => string,
  extractRaw: (resp: unknown) => unknown[],
): { sets: ReadonlyArray<LogSet> | null; error: string | null } {
  const [sets, setSets] = useState<ReadonlyArray<LogSet> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const since = new Date(Date.now() - days * DAY_MS).toISOString();
        const resp = await apiFetch<unknown>(makePath(since));
        if (cancelled) return;
        const parsed: LogSet[] = [];
        for (const raw of extractRaw(resp)) {
          const p = LogSetSchema.safeParse(raw);
          if (p.success) parsed.push(p.data);
        }
        setSets(parsed);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "load_failed");
      }
    })();
    return () => {
      cancelled = true;
    };
    // `days` is the only real input: makePath/extractRaw are inline lambdas at
    // both callsites (new identity every render), and apiFetch is a stable
    // per-surface seam. Same deps the FatigueDrift original ran on — widening
    // them here would re-fetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);
  return { sets, error };
}
